/**
 * Static demo data + per-platform rules powering the wireframe-fidelity screens.
 * Mirrors `dev-spec/v1/contenthub-app.jsx` so the SPA renders fully populated
 * before the GraphQL backend is wired (DEV-3 backlog). Once the API is live,
 * routes should swap these arrays for urql queries — the shapes are intentionally
 * close to `contenthub-shared` types.
 */

export type PlatformId =
  | 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'tiktok'
  | 'youtube' | 'pinterest' | 'threads' | 'snapchat';

export type PostStatus =
  | 'draft' | 'pending' | 'review' | 'approved' | 'scheduled' | 'published';

export interface Post {
  id: number;
  title: string;
  caption: string;
  platforms: PlatformId[];
  status: PostStatus;
  day?: number;
  time?: string;
}

export interface ContentItem {
  id: number;
  title: string;
  type: 'article' | 'pdf' | 'link' | 'post';
  source: string;
  sourceId: number | null;
  added: string;
  posts: number;
  status: 'new' | 'reviewed' | 'used';
  relevance: number;
  hasImage: boolean;
  excerpt: string;
}

export interface FeedItem {
  id: number;
  title: string;
  type: 'article' | 'pdf' | 'link' | 'post';
  sources: string[];
  added: string;
  relevance: number;
  hasImage: boolean;
  excerpt: string;
}

export const platforms: { id: PlatformId; name: string; color: string; icon: string }[] = [
  { id: 'linkedin', name: 'LinkedIn', color: '#0A66C2', icon: 'in' },
  { id: 'twitter', name: 'X / Twitter', color: '#1a1a1a', icon: '𝕏' },
  { id: 'facebook', name: 'Facebook', color: '#1877F2', icon: 'f' },
  { id: 'instagram', name: 'Instagram', color: '#E4405F', icon: '◷' },
  { id: 'tiktok', name: 'TikTok', color: '#1a1a1a', icon: '♪' },
  { id: 'youtube', name: 'YouTube', color: '#FF0000', icon: '▶' },
  { id: 'pinterest', name: 'Pinterest', color: '#E60023', icon: 'P' },
  { id: 'threads', name: 'Threads', color: '#1a1a1a', icon: '@' },
  { id: 'snapchat', name: 'Snapchat', color: '#FFFC00', icon: '◐' },
];

// Per-platform character limits now live in contenthub-shared (`characterLimits`)
// — the single source of truth consumed by the composer. Import from
// '@contenthub/shared' rather than re-declaring here.

export const samplePosts: Post[] = [
  { id: 1, title: '5 AI trends reshaping marketing', caption: 'Artificial intelligence is transforming how we approach marketing...', platforms: ['linkedin', 'twitter'], status: 'pending' },
  { id: 2, title: 'Product launch announcement', caption: "We're thrilled to announce the launch of ContentHub 2.0...", platforms: ['linkedin', 'facebook'], status: 'approved' },
  { id: 11, title: 'AI trends post', caption: 'Quick take on AI trends in 2026...', platforms: ['linkedin'], status: 'scheduled', day: 1, time: '9:00' },
  { id: 12, title: '5 AI trends', caption: '5 AI trends reshaping marketing...', platforms: ['linkedin', 'twitter'], status: 'scheduled', day: 3, time: '9:00' },
  { id: 13, title: 'Product launch', caption: 'Big news — ContentHub 2.0 is here!', platforms: ['facebook'], status: 'scheduled', day: 4, time: '10:00' },
];

export const contentItems: ContentItem[] = [
  { id: 1, title: 'The future of AI in content marketing', type: 'article', source: 'TechCrunch', sourceId: 1, added: '2 hours ago', posts: 0, status: 'reviewed', relevance: 92, hasImage: true, excerpt: 'Generative AI is reshaping how marketing teams research, draft, and measure campaigns — here are the shifts worth acting on this quarter.' },
  { id: 5, title: '10 tips for better social engagement', type: 'article', source: 'Hacker News', sourceId: 2, added: '4 hours ago', posts: 0, status: 'reviewed', relevance: 81, hasImage: false, excerpt: 'A practical roundup: hooks in the first line, native video, replying fast, and posting at audience peak times.' },
  { id: 3, title: 'How startups are using automation', type: 'link', source: 'URL import', sourceId: null, added: 'Yesterday', posts: 2, status: 'reviewed', relevance: 74, hasImage: true, excerpt: 'Case studies of lean teams automating outbound, support, and content ops without losing brand voice.' },
  { id: 2, title: 'Q2 Product roadmap.pdf', type: 'pdf', source: 'Manual upload', sourceId: null, added: 'Yesterday', posts: 0, status: 'reviewed', relevance: 40, hasImage: false, excerpt: '12-page internal roadmap. Mostly internal — likely low value for public posts.' },
  { id: 4, title: 'Industry report: SaaS trends 2025', type: 'pdf', source: 'Manual upload', sourceId: null, added: 'Apr 12', posts: 4, status: 'used', relevance: 88, hasImage: true, excerpt: 'Benchmark data on retention, pricing, and AI adoption across 400 SaaS companies. Lots of quotable stats.' },
];

export const recentSources = [
  { id: 1, name: 'TechCrunch', type: 'RSS', lastFetch: '2 hours ago', newItems: 3 },
  { id: 2, name: 'Hacker News', type: 'RSS', lastFetch: '1 hour ago', newItems: 5 },
  { id: 3, name: 'Industry Blog', type: 'Scrape', lastFetch: '30 min ago', newItems: 1 },
];

export interface ContentSource {
  id: number;
  name: string;
  type: string;
  url: string;
  health: 'ok' | 'warning' | 'error';
  frequency: string;
  lastFetch: string;
  newItems: number;
  total: number;
  error?: string;
}

export const contentSources: ContentSource[] = [
  { id: 1, name: 'TechCrunch', type: 'RSS Feed', url: 'techcrunch.com/feed', health: 'ok', frequency: 'Every hour', lastFetch: '2 hours ago', newItems: 1, total: 12 },
  { id: 2, name: 'Hacker News', type: 'RSS Feed', url: 'news.ycombinator.com/rss', health: 'ok', frequency: 'Every hour', lastFetch: '1 hour ago', newItems: 1, total: 8 },
  { id: 3, name: 'Company blog', type: 'Web scrape', url: 'acme.com/blog', health: 'error', frequency: 'Daily', lastFetch: '3 days ago', newItems: 0, total: 5, error: 'Last fetch failed — page structure changed. Re-check selector.' },
  { id: 4, name: 'Industry News', type: 'RSS Feed', url: 'industrynews.com/rss', health: 'warning', frequency: 'Every 6 hours', lastFetch: '8 hours ago', newItems: 0, total: 15, error: 'Overdue — no successful fetch in 8h.' },
];

export const feedQueue: FeedItem[] = [
  { id: 101, title: 'OpenAI announces new model aimed at marketers', type: 'article', sources: ['TechCrunch', 'The Verge', 'VentureBeat'], added: '12 min ago', relevance: 94, hasImage: true, excerpt: 'A new model promises better long-form drafting and on-brand tone control — covered widely across tech press.' },
  { id: 102, title: 'Study: short-form video drives 2× the engagement of static posts', type: 'article', sources: ['Hacker News'], added: '40 min ago', relevance: 86, hasImage: false, excerpt: 'Analysis of 1M posts shows vertical video out-performing images on every major network.' },
  { id: 103, title: 'Competitor launches a built-in scheduling feature', type: 'link', sources: ['Industry News', 'TechCrunch'], added: '1 hour ago', relevance: 79, hasImage: true, excerpt: 'Worth a reaction post or comparison angle — two outlets covering the same launch.' },
  { id: 104, title: 'Weekly SEO roundup #214', type: 'article', sources: ['SEO Blog'], added: '2 hours ago', relevance: 54, hasImage: false, excerpt: 'Mostly link-building tactics; little that maps to social.' },
  { id: 105, title: 'Quarterly numbers press release', type: 'article', sources: ['PR Newswire'], added: '3 hours ago', relevance: 28, hasImage: false, excerpt: 'Boilerplate financial release — low relevance for social.' },
];

export const connections: { id: PlatformId; name: string; status: 'connected' | 'disconnected'; account: string | null }[] = [
  { id: 'linkedin', name: 'LinkedIn', status: 'connected', account: 'Acme Corp Page' },
  { id: 'twitter', name: 'X / Twitter', status: 'connected', account: '@acmecorp' },
  { id: 'facebook', name: 'Facebook', status: 'disconnected', account: null },
  { id: 'instagram', name: 'Instagram', status: 'disconnected', account: null },
  { id: 'tiktok', name: 'TikTok', status: 'disconnected', account: null },
  { id: 'youtube', name: 'YouTube', status: 'disconnected', account: null },
  { id: 'pinterest', name: 'Pinterest', status: 'disconnected', account: null },
  { id: 'threads', name: 'Threads', status: 'disconnected', account: null },
  { id: 'snapchat', name: 'Snapchat', status: 'disconnected', account: null },
];

export const mediaItems = [
  { id: 1, type: 'image', name: 'ai-trends-hero.jpg', size: '1.2 MB', folder: 'Brand assets', uploaded: '2 days ago' },
  { id: 2, type: 'image', name: 'product-demo-1.png', size: '2.4 MB', folder: 'Product photos', uploaded: '1 week ago' },
  { id: 3, type: 'image', name: 'team-photo.jpg', size: '3.1 MB', folder: 'Brand assets', uploaded: '2 weeks ago' },
  { id: 4, type: 'video', name: 'promo-video.mp4', size: '12.5 MB', folder: 'Videos', uploaded: '3 days ago' },
  { id: 5, type: 'image', name: 'logo-variants.png', size: '0.8 MB', folder: 'Brand assets', uploaded: '1 month ago' },
  { id: 6, type: 'gif', name: 'celebration.gif', size: '1.5 MB', folder: 'Brand assets', uploaded: '4 days ago' },
  { id: 7, type: 'audio', name: 'podcast-intro.mp3', size: '2.1 MB', folder: 'Audio', uploaded: '1 week ago' },
  { id: 8, type: 'image', name: 'infographic-q1.png', size: '4.2 MB', folder: 'Product photos', uploaded: '5 days ago' },
];

export const mediaFolders = [
  { id: 'all', name: 'All media', count: 47, icon: '◉' },
  { id: 'brand', name: 'Brand assets', count: 12, icon: '◬' },
  { id: 'products', name: 'Product photos', count: 24, icon: '▣' },
  { id: 'videos', name: 'Videos', count: 5, icon: '▶' },
  { id: 'audio', name: 'Audio', count: 3, icon: '♪' },
  { id: 'docs', name: 'Documents', count: 3, icon: '▤' },
];

export const templates: { id: number; name: string; platforms: PlatformId[]; uses: number; category: string }[] = [
  { id: 1, name: 'Product launch', platforms: ['linkedin', 'twitter', 'facebook'], uses: 23, category: 'Marketing' },
  { id: 2, name: 'Weekly tip', platforms: ['linkedin', 'twitter'], uses: 45, category: 'Engagement' },
  { id: 3, name: 'Customer story', platforms: ['linkedin', 'facebook', 'instagram'], uses: 12, category: 'Social proof' },
  { id: 4, name: 'Industry news', platforms: ['linkedin', 'twitter'], uses: 67, category: 'News' },
  { id: 5, name: 'Behind the scenes', platforms: ['instagram', 'facebook'], uses: 18, category: 'Brand' },
  { id: 6, name: 'Job posting', platforms: ['linkedin'], uses: 8, category: 'Hiring' },
];

export const typeMeta: Record<ContentItem['type'], { icon: string; label: string }> = {
  article: { icon: '📰', label: 'Article' },
  pdf: { icon: '▤', label: 'PDF' },
  link: { icon: '🔗', label: 'Link' },
  post: { icon: '◰', label: 'Post' },
};

/** Project list for the dashboard / switchers when the API isn't wired. */
export const demoProjects = ['Acme Corp', 'TechStart Inc', 'GreenLeaf Co'];
